import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CINEMA_CATEGORIES, detectSource } from "@/lib/cinema";
import { Film, Play, Plus, Search, Sparkles, Star, X, Lock, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

function PosterCard({ movie, isAdmin, onDelete, onEdit }) {
  return (
    <div
      data-testid={`cinema-poster-${movie.id}`}
      className="group relative shrink-0 w-44 sm:w-52 md:w-56 rounded-xl overflow-hidden border border-jj bg-cream-surface transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <Link to={`/cinema/${movie.id}`} className="block">
      <div className="aspect-[2/3] bg-jjblue/10 overflow-hidden">
        {movie.poster_url ? (
          <img
            src={movie.poster_url}
            alt={movie.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-burgundy/40">
            <Film className="h-12 w-12" strokeWidth={1.2} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-jjblue/85 via-jjblue/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute inset-0 flex items-end p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-cream">
            <Play className="h-3.5 w-3.5 fill-current" /> Watch now
          </span>
        </div>
        {movie.locked && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-jjblue/90 text-cream text-[10px] tracking-wide px-2 py-1 rounded-full">
            <Lock className="h-3 w-3" /> Premium
          </span>
        )}
        {movie.featured && !movie.locked && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-gold text-jjblue text-[10px] font-semibold px-2 py-1 rounded-full">
            <Star className="h-3 w-3 fill-current" /> Featured
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="font-serif text-base text-jjblue leading-tight line-clamp-2">{movie.title}</div>
        <div className="mt-1 text-[11px] text-[var(--jj-muted)] flex items-center gap-1.5">
          {movie.year && <span>{movie.year}</span>}
          {movie.duration_min && (<><span>·</span><span>{movie.duration_min} min</span></>)}
          {movie.rating && (<><span>·</span><span>{movie.rating}</span></>)}
        </div>
      </div>
      </Link>
      {isAdmin && (
        <>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit?.(movie); }}
            className="absolute bottom-3 right-11 p-1.5 rounded-full bg-jjblue/85 text-cream opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gold hover:text-jjblue"
            title="Edit movie"
            aria-label={`Edit ${movie.title}`}
            data-testid={`cinema-edit-${movie.id}`}
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.7} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete?.(movie); }}
            className="absolute bottom-3 right-3 p-1.5 rounded-full bg-jjblue/85 text-cream opacity-0 group-hover:opacity-100 transition-opacity hover:bg-burgundy"
            title="Delete movie"
            aria-label={`Delete ${movie.title}`}
            data-testid={`cinema-delete-${movie.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.7} />
          </button>
        </>
      )}
    </div>
  );
}

function Row({ title, movies, emptyHint, isAdmin, onDelete, onEdit }) {
  if (!movies || movies.length === 0) {
    return (
      <div className="mt-8">
        <h2 className="font-serif text-2xl text-jjblue">{title}</h2>
        <p className="mt-2 text-sm text-[var(--jj-muted)]">{emptyHint}</p>
      </div>
    );
  }
  return (
    <section className="mt-10" data-testid={`cinema-row-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-2xl sm:text-3xl text-jjblue">{title}</h2>
        <span className="text-xs text-[var(--jj-muted)]">{movies.length} title{movies.length !== 1 && "s"}</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 -mx-6 px-6 snap-x snap-mandatory scroll-smooth">
        {movies.map((m) => (
          <div key={m.id} className="snap-start"><PosterCard movie={m} isAdmin={isAdmin} onDelete={onDelete} onEdit={onEdit} /></div>
        ))}
      </div>
    </section>
  );
}

function EditMovieDialog({ movie, onClose, onSaved }) {
  const [title, setTitle] = useState(movie?.title || "");
  const [description, setDescription] = useState(movie?.description || "");
  const [category, setCategory] = useState(movie?.category || "films");
  const [year, setYear] = useState(movie?.year || "");
  const [duration, setDuration] = useState(movie?.duration_min || "");
  const [director, setDirector] = useState(movie?.director || "");
  const [rating, setRating] = useState(movie?.rating || "");
  const [tags, setTags] = useState((movie?.tags || []).join(", "));
  const [posterUrl, setPosterUrl] = useState(movie?.poster_url || "");
  const [isPremium, setIsPremium] = useState(!!movie?.is_premium);
  const [featured, setFeatured] = useState(!!movie?.featured);
  const [busy, setBusy] = useState(false);

  if (!movie) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        category,
        year: year ? Number(year) : null,
        duration_min: duration ? Number(duration) : null,
        director: director.trim() || null,
        rating: rating.trim() || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        poster_url: posterUrl.trim() || null,
        is_premium: isPremium,
        featured,
      };
      const { data } = await api.patch(`/cinema/movies/${movie.id}`, payload);
      toast.success("Movie updated");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update movie");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="cinema-edit-dialog">
      <div className="jj-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy" data-testid="cinema-edit-close">
          <X className="h-5 w-5" />
        </button>
        <div className="overline text-burgundy">Admin · Edit movie</div>
        <h2 className="font-serif text-3xl text-jjblue mt-2">{movie.title}</h2>
        <p className="mt-1 text-xs text-[var(--jj-muted)]">Video source URL cannot be changed here — delete and re-add if the video changed.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input className="input-jj" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cinema-edit-title" />
          <textarea className="input-jj" rows={3} placeholder="Short description" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="cinema-edit-desc" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-jj" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cinema-edit-category">
              {CINEMA_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
            <input className="input-jj" placeholder="Rating (G, PG, etc.)" value={rating} onChange={(e) => setRating(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input className="input-jj" placeholder="Year" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} />
            <input className="input-jj" placeholder="Duration (min)" inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} />
            <input className="input-jj" placeholder="Director" value={director} onChange={(e) => setDirector(e.target.value)} />
          </div>
          <input className="input-jj" placeholder="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} />
          <input className="input-jj" placeholder="Poster image URL (optional)" value={posterUrl} onChange={(e) => setPosterUrl(e.target.value)} />
          <div className="flex flex-wrap gap-5 pt-2 text-sm text-jjblue">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="cinema-edit-featured" />
              <Sparkles className="h-4 w-4 text-gold" /> Featured on hero
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} data-testid="cinema-edit-premium" />
              <Lock className="h-4 w-4 text-burgundy" /> Premium tier only
            </label>
          </div>
          <div className="flex items-center justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="cinema-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddMovieDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("films");
  const [url, setUrl] = useState("");
  const [year, setYear] = useState("");
  const [duration, setDuration] = useState("");
  const [director, setDirector] = useState("");
  const [rating, setRating] = useState("");
  const [tags, setTags] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const src = detectSource(url);
    if (!src) {
      toast.error("Please paste a valid YouTube or Vimeo URL");
      return;
    }
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        category,
        source_type: src.source_type,
        source_id: src.source_id,
        year: year ? Number(year) : null,
        duration_min: duration ? Number(duration) : null,
        director: director.trim() || null,
        rating: rating.trim() || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        is_premium: isPremium,
        featured,
      };
      const { data } = await api.post("/cinema/movies", payload);
      toast.success("Added to Faith Cinema");
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add movie");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="cinema-add-dialog">
      <div className="jj-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy" data-testid="cinema-add-close">
          <X className="h-5 w-5" />
        </button>
        <div className="overline text-burgundy">Admin · Add to library</div>
        <h2 className="font-serif text-3xl text-jjblue mt-2">New Faith Cinema title</h2>
        <p className="mt-2 text-sm text-[var(--jj-muted)]">Paste a public YouTube or Vimeo URL. Embed-only; we never re-host copyrighted media.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input className="input-jj" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cinema-add-title" />
          <input className="input-jj" placeholder="YouTube or Vimeo URL (e.g. https://youtu.be/...)" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="cinema-add-url" />
          <textarea className="input-jj" rows={3} placeholder="Short description" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="cinema-add-desc" />
          <div className="grid grid-cols-2 gap-3">
            <select className="input-jj" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cinema-add-category">
              {CINEMA_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
            <input className="input-jj" placeholder="Rating (G, PG, etc.)" value={rating} onChange={(e) => setRating(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input className="input-jj" placeholder="Year" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} />
            <input className="input-jj" placeholder="Duration (min)" inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} />
            <input className="input-jj" placeholder="Director" value={director} onChange={(e) => setDirector(e.target.value)} />
          </div>
          <input className="input-jj" placeholder="Tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} />
          <div className="flex flex-wrap gap-5 pt-2 text-sm text-jjblue">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="cinema-add-featured" />
              <Sparkles className="h-4 w-4 text-gold" /> Featured on hero
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} data-testid="cinema-add-premium" />
              <Lock className="h-4 w-4 text-burgundy" /> Premium tier only
            </label>
          </div>
          <div className="flex items-center justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="cinema-add-submit">{busy ? "Adding…" : "Add to library"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Cinema() {
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editMovie, setEditMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cinema/movies", { params: { limit: 200 } });
      setMovies(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return movies;
    const needle = q.toLowerCase();
    return movies.filter((m) =>
      [m.title, m.description, m.director, ...(m.tags || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(needle)
    );
  }, [movies, q]);

  const featured = filtered.find((m) => m.featured) || filtered[0];
  const byCategory = useMemo(() => {
    const out = {};
    for (const c of CINEMA_CATEGORIES) out[c.slug] = [];
    for (const m of filtered) {
      if (out[m.category]) out[m.category].push(m);
    }
    return out;
  }, [filtered]);

  const isAdmin = user?.role === "admin";

  const deleteMovie = async (movie) => {
    if (!window.confirm(`Remove "${movie.title}" from Faith Cinema? This cannot be undone.`)) return;
    try {
      await api.delete(`/cinema/movies/${movie.id}`);
      setMovies((arr) => arr.filter((m) => m.id !== movie.id));
      toast.success("Movie removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete movie");
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-jjblue via-jjblue to-burgundy" />
        {featured?.poster_url && (
          <img
            src={featured.poster_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity"
            aria-hidden="true"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-jjblue via-jjblue/80 to-jjblue/40" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="overline text-gold flex items-center gap-2">
            <Film className="h-3.5 w-3.5" /> Faith Cinema
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream mt-3 max-w-3xl leading-[1.05]">
            Free faith-filled films, sermons, and documentaries — all in one sanctuary.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-cream/80 max-w-2xl">
            Hand-picked, family-safe content streamed from trusted Christian creators. No ads. No noise. Just stories worth watching.
          </p>

          {featured && (
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={`/cinema/${featured.id}`}
                className="btn-gold flex items-center gap-2 !py-3 !px-6"
                data-testid="cinema-hero-play"
              >
                <Play className="h-4 w-4 fill-current" /> Play &ldquo;{featured.title.slice(0, 38)}{featured.title.length > 38 ? "…" : ""}&rdquo;
              </Link>
              <span className="text-cream/70 text-sm">
                {featured.year && <>{featured.year} · </>}
                {featured.duration_min && <>{featured.duration_min} min · </>}
                {featured.category && <span className="capitalize">{featured.category}</span>}
              </span>
            </div>
          )}

          {/* Search + admin */}
          <div className="mt-10 flex flex-wrap items-center gap-3 max-w-2xl">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cream/60" />
              <input
                className="w-full bg-cream/10 border border-cream/20 rounded-full pl-10 pr-4 py-2.5 text-sm text-cream placeholder:text-cream/50 focus:outline-none focus:border-gold"
                placeholder="Search films, directors, topics…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="cinema-search"
              />
            </div>
            {isAdmin && (
              <button
                onClick={() => setAddOpen(true)}
                className="btn-gold !py-2.5 !px-5 text-sm flex items-center gap-1.5"
                data-testid="cinema-add-btn"
              >
                <Plus className="h-4 w-4" /> Add movie
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Rows */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-20">
        {loading ? (
          <div className="py-20 text-center text-[var(--jj-muted)]">Loading library…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Film className="h-12 w-12 text-burgundy/40 mx-auto" strokeWidth={1.2} />
            <h2 className="font-serif text-2xl text-jjblue mt-4">No titles match &ldquo;{q}&rdquo;</h2>
            <p className="mt-2 text-sm text-[var(--jj-muted)]">Try a different search, or browse the full library.</p>
          </div>
        ) : (
          <>
            {filtered.some((m) => m.featured) && (
              <Row title="✦ Featured" movies={filtered.filter((m) => m.featured)} isAdmin={isAdmin} onDelete={deleteMovie} onEdit={setEditMovie} />
            )}
            {CINEMA_CATEGORIES.map((c) => (
              <Row
                key={c.slug}
                title={c.label}
                movies={byCategory[c.slug]}
                isAdmin={isAdmin}
                onDelete={deleteMovie}
                onEdit={setEditMovie}
                emptyHint={isAdmin ? "Empty for now — use 'Add movie' above to curate this row." : "Coming soon. Check back as the library grows."}
              />
            ))}
          </>
        )}
      </div>

      <AddMovieDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => load()} />
      {editMovie && (
        <EditMovieDialog
          movie={editMovie}
          onClose={() => setEditMovie(null)}
          onSaved={(updated) => setMovies((arr) => arr.map((m) => m.id === updated.id ? updated : m))}
        />
      )}
    </div>
  );
}
